import { Box, Button, List, Stack, Typography, useTheme } from '@mui/material'
import { useMemo, useState } from 'react'
import {
  EmphasizedNumber,
  Fade,
  GachaTicketThumbnail,
  Icons,
  InformationModal,
  MenuListItem,
} from 'utoniq-ui'

import {
  CryptoWalletType,
  GachaTicketConnectionFragment,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  CreateCryptoWalletConfirmModal,
  CryptoWalletAddress,
  CryptoWalletBalance,
  CryptoWalletConnectDrawer,
  MetamaskAccountIcon,
  MetamaskWalletConnectInfoModal,
} from '@/components/organisms/nft'
import { Action as PasswordSettingFormAction } from '@/components/pages/PasswordSettings/template'
import { config } from '@/config'
import { useMessage, User, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { captureError } from '@/core/errors'
import { getPlatform } from '@/core/native/app'
import { isMetamaskInstalled } from '@/core/web3/metamask'
import { filterHiddenItem } from '@/lib/array'

import { WalletPasswordSettingModal } from './WalletPasswordSettingModal'

export * from './fragments'

export type State = {
  loading: boolean
  user: User | null
  pointBalance: string | null
  ticket: string | null
  gachaTicket: Maybe<GachaTicketConnectionFragment>
  cryptoWallet: {
    displayName: string | null
    walletAddress: string | null
    balance: string | null
    walletType: string | null
  }
}

export type Action = {
  connectMetamask: () => Promise<void>
  connectToWeb3auth: () => Promise<void>
  openTorusWallet: () => Promise<void>
  onSubmitPasswordSetting: PasswordSettingFormAction['onSubmitWalletPassword']
}

type WalletChargePointProps = {
  loading: boolean
  pointBalance: string | null
  ticket: string | null
  gachaTicket: State['gachaTicket']
  cryptoWallet: State['cryptoWallet']
  onConnectWallet: () => void
  onOpenWallet: () => Promise<void>
}

type ListProps = {
  title: string
  disable?: boolean
  href?: string
}

export const WalletTemplate: Template<State, Action> = ({
  state,
  connectMetamask,
  connectToWeb3auth,
  openTorusWallet,
  onSubmitPasswordSetting,
}) => {
  const { t } = useTranslation('wallet')
  const { routerNames } = useRouter()
  const [openNftConnectDrawer, setOpenNftConnectDrawer] = useState(false)
  const { showError, showMessage } = useMessage()
  const [connecting, setConnecting] = useState(false)
  const [openWalletCreateConfirmModal, setOpenWalletCreateConfirmModal] =
    useState(false)
  const [openPasswordRequireModal, setOpenPasswordRequireModal] =
    useState(false)
  const [openWalletPasswordSettingModal, setOpenWalletPasswordSettingModal] =
    useState(false)
  const [
    openMetamaskWalletConnectInfoModal,
    setOpenMetamaskWalletConnectInfoModal,
  ] = useState(false)

  const onConnectWeb3auth = () => {
    setConnecting(true)
    return connectToWeb3auth()
      .catch(e => {
        captureError(e, 'walletTemplate')
        showError(e)
      })
      .finally(() => {
        setConnecting(false)
        setOpenWalletCreateConfirmModal(false)
        setOpenNftConnectDrawer(false)
      })
  }

  const onConnectMetamask = () => {
    setConnecting(true)
    return connectMetamask()
      .then(() => {
        showMessage(t('connected'))
      })
      .catch(e => {
        captureError(e, 'walletTemplate')
        showError(e)
      })
      .finally(() => {
        setConnecting(false)
        setOpenNftConnectDrawer(false)
      })
  }

  return (
    <BaseLayout
      header={{
        back: true,
        title: t('title'),
      }}
    >
      <Head title={routerNames.Wallet} />
      <WalletChargePoint
        loading={state.loading}
        pointBalance={state.pointBalance}
        ticket={state.ticket}
        gachaTicket={state.gachaTicket}
        cryptoWallet={state.cryptoWallet}
        onConnectWallet={() => setOpenNftConnectDrawer(true)}
        onOpenWallet={() => openTorusWallet()}
      />
      <MenuCell isPurchasable={!!state.gachaTicket?.isPurchasable} />
      <CreateCryptoWalletConfirmModal
        open={openWalletCreateConfirmModal}
        loading={connecting}
        onClose={() => setOpenWalletCreateConfirmModal(false)}
        onCreate={onConnectWeb3auth}
      />
      <CryptoWalletConnectDrawer
        open={openNftConnectDrawer}
        onClose={() => setOpenNftConnectDrawer(false)}
        connecting={connecting}
        onConnectWallet={walletType => {
          switch (walletType) {
            case 'web3auth':
              return setOpenWalletCreateConfirmModal(true)
            case 'metamask':
              /**
               * @description
               * パスワード認証未済の場合はパスワード登録を促す
               */
              if (
                !state.user?.providers.some(
                  provider => provider.providerId === 'password'
                )
              ) {
                return setOpenPasswordRequireModal(true)
              }

              /**
               * @description
               * nativeアプリ内且つメタマスクアプリ内でない場合、動線表示用のモーダルを表示
               */
              if (!isMetamaskInstalled() && getPlatform() !== 'web') {
                return setOpenMetamaskWalletConnectInfoModal(true)
              }

              return onConnectMetamask()
          }
        }}
      />

      <PasswordRequireModal
        open={openPasswordRequireModal}
        onClose={() => setOpenPasswordRequireModal(false)}
        onClick={() => setOpenWalletPasswordSettingModal(true)}
      />

      <WalletPasswordSettingModal
        open={openWalletPasswordSettingModal}
        onClose={() => {
          setOpenPasswordRequireModal(false)
          setOpenWalletPasswordSettingModal(false)
        }}
        onSubmit={onSubmitPasswordSetting}
        email={state.user?.email}
      />

      <MetamaskWalletConnectInfoModal
        open={openMetamaskWalletConnectInfoModal}
        onClose={() => setOpenMetamaskWalletConnectInfoModal(false)}
      />
    </BaseLayout>
  )
}

export const WalletChargePoint: Cell<WalletChargePointProps> = ({
  loading,
  pointBalance,
  ticket,
  gachaTicket,
  cryptoWallet,
  onConnectWallet,
  onOpenWallet,
}) => {
  const { t } = useTranslation('wallet')
  const { routerPages } = useRouter()

  return (
    <Stack spacing={4} sx={{ pt: 4, mx: 5 }}>
      {!!config.modules.prepaidPoint && (
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          sx={{
            pt: 5,
            pb: 6,
            borderRadius: 2,
            backgroundColor: 'grey.50',
          }}
        >
          <Typography variant="h6" color="text.secondary">
            {t('pointBalance')}
          </Typography>
          <Point point={pointBalance} loading={loading} />
          {config.modules.prepaidPoint.chargeable && (
            <Button href={routerPages.PrepaidPointCharge}>{t('charge')}</Button>
          )}
        </Stack>
      )}
      {config.modules.nft ? (
        loading ? (
          <Box
            sx={{
              bgcolor: 'grey.50',
              height: 200,
              width: '100%',
              borderRadius: 2,
            }}
          />
        ) : cryptoWallet.walletAddress ? (
          <WalletConnectCell
            cryptoWallet={cryptoWallet}
            onOpenWallet={onOpenWallet}
          />
        ) : (
          <WalletNotConnectedCell onConnectWallet={onConnectWallet} />
        )
      ) : null}
    </Stack>
  )
}

type MenuProps = {
  isPurchasable: boolean
}
const MenuCell: Cell<MenuProps> = ({ isPurchasable }) => {
  const { t } = useTranslation('wallet')
  const { routerPages } = useRouter()

  const list = useMemo<ListProps[]>(
    () => [
      {
        title: t('pointOrders'),
        hidden: !config.modules.prepaidPoint,
        href: routerPages.PointOrders,
      },
      {
        title: t('ticketOrders'),
        hidden: !config.modules.gacha,
        href: routerPages.TicketOrders,
      },
    ],
    [routerPages.PointOrders, routerPages.TicketOrders, t]
  )

  return (
    <CellFrame sx={{ pt: 6 }}>
      <Typography variant="h6" color="text.secondary" sx={{ py: 2, px: 5 }}>
        {t('menu')}
      </Typography>
      <List sx={{ pt: 2 }}>
        {filterHiddenItem(list).map((item, index) => (
          <MenuListItem
            key={index}
            divider
            title={item.title}
            href={item.href}
            sx={{
              height: { xs: 52, md: 56 },
              bgcolor: 'grey.50',
            }}
            primaryTypographyProps={{
              variant: 'body1',
            }}
          />
        ))}
      </List>
    </CellFrame>
  )
}

type PointProps = {
  point: string | number | null
  loading: boolean
}
const Point: Cell<PointProps> = ({ point, loading }) => {
  return (
    <CellFrame>
      <Stack direction="row" sx={{ mt: 1, mb: 4 }} alignItems="center">
        <Icons.Point fontSize="large" />

        <Box sx={{ ml: 2, minHeight: 36, minWidth: 36 }}>
          {loading ? (
            <Box />
          ) : (
            <Fade>
              <EmphasizedNumber variant="h1" align="center">
                {loading ? null : point ? point : 0}
              </EmphasizedNumber>
            </Fade>
          )}
        </Box>
      </Stack>
    </CellFrame>
  )
}

type TicketProps = {
  ticket: string | number | null
  loading: boolean
  gachaTicket: Maybe<GachaTicketConnectionFragment>
}
const Ticket: Cell<TicketProps> = ({ ticket, loading, gachaTicket }) => {
  return (
    <CellFrame>
      <Stack direction="row" sx={{ mt: 1 }} alignItems="center">
        <GachaTicketThumbnail
          src={gachaTicket?.thumbnails?.standard?.smallUri || ''}
        />
        <Box sx={{ ml: 2, minHeight: 36, minWidth: 36 }}>
          {loading ? (
            <Box />
          ) : (
            <Fade>
              <EmphasizedNumber variant="h1" align="center">
                {loading ? null : ticket ? ticket : 0}
              </EmphasizedNumber>
            </Fade>
          )}
        </Box>
      </Stack>
    </CellFrame>
  )
}

const WalletConnectCell: Cell<{
  cryptoWallet: State['cryptoWallet']
  onOpenWallet: Action['openTorusWallet']
}> = ({ cryptoWallet, onOpenWallet }) => {
  const { t } = useTranslation('wallet')

  return (
    <CellFrame>
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{
          py: 5,
          borderRadius: 2,
          backgroundColor: 'grey.50',
        }}
      >
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          {t('connectWallet')}
        </Typography>
        <MetamaskAccountIcon walletAddress={cryptoWallet.walletAddress || ''} />
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {cryptoWallet.displayName}
        </Typography>
        <CryptoWalletAddress cryptoWalletAddress={cryptoWallet.walletAddress} />
        <Stack sx={{ my: 4 }}>
          <CryptoWalletBalance cryptoWalletBalance={cryptoWallet.balance} />
        </Stack>

        {cryptoWallet.walletType === CryptoWalletType.Web3auth && (
          <Button onClick={onOpenWallet}>{t('openCryptoWallet')}</Button>
        )}
      </Stack>
    </CellFrame>
  )
}

type WalletNotConnectedCellProps = {
  onConnectWallet: () => void
}
const WalletNotConnectedCell: Cell<WalletNotConnectedCellProps> = ({
  onConnectWallet,
}) => {
  const { t } = useTranslation('wallet')

  return (
    <CellFrame>
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{
          py: 5,
          borderRadius: 2,
          backgroundColor: 'grey.50',
        }}
      >
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          {t('connectWallet')}
        </Typography>
        <Stack direction="row" sx={{ px: 7, mb: 3 }}>
          <Icons.Info color="warning" sx={{ mr: 2 }} />
          <Typography variant="caption">
            {t('connectWalletDescription')}
          </Typography>
        </Stack>
        <Button onClick={onConnectWallet}>{t('connect')}</Button>
      </Stack>
    </CellFrame>
  )
}

type PasswordRequireModalProps = {
  open: boolean
  onClose: () => void
  onClick: () => void
}
const PasswordRequireModal = ({
  open,
  onClose,
  onClick,
}: PasswordRequireModalProps) => {
  const { t } = useTranslation('wallet')
  const { zIndex } = useTheme()

  return (
    <InformationModal
      open={open}
      onClose={onClose}
      title={t('passwordRequireModalTitle')}
      content={t('passwordRequireModalContent')}
      buttons={
        <Button size="large" sx={{ width: 212 }} onClick={onClick}>
          {t('toSetting')}
        </Button>
      }
      cancelText={t('close')}
      sx={{ zIndex: zIndex.modal + 2 }}
    />
  )
}
